import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";

/**
 * Drives the invite-email "set your password" landing page.
 *
 *   useVerifyResetLink({ uidb64, token }) — fires on mount, tells the
 *     page whether the link is still valid before the user types.
 *   useSetPassword() — POSTs the new password once the user submits.
 */

interface ResetLinkInfo {
  email: string;
  first_name: string;
  last_name: string;
}

interface LinkRef {
  uidb64?: string;
  token?: string;
}

export function useVerifyResetLink({ uidb64, token }: LinkRef) {
  return useQuery<ResetLinkInfo>({
    enabled: !!uidb64 && !!token,
    queryKey: ["set-password", "verify", uidb64, token],
    retry: false,                          // bad links shouldn't loop
    staleTime: Infinity,
    queryFn: async () => {
      // Prime the CSRF cookie so this POST and the follow-up confirm
      // both carry a valid X-CSRFToken header.
      await api.get("/auth/csrf/");
      const { data } = await api.post<ResetLinkInfo>(
        "/auth/set-password/verify/",
        { uidb64, token },
      );
      return data;
    },
  });
}

interface ConfirmInput {
  uidb64: string;
  token:  string;
  password: string;
}

export function useSetPassword() {
  return useMutation({
    mutationFn: async (input: ConfirmInput) => {
      const { data } = await api.post<{ detail: string }>(
        "/auth/set-password/confirm/",
        input,
      );
      return data;
    },
  });
}
